import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Phone, MessageCircle, Check, MapPin, Clock, Users, Car, FileText, Calculator } from 'lucide-react';
import { Button } from '../components/ui/button';
import { COMPANY_INFO } from '../mock';
import { InternalLinksSection } from '../components/InternalLinksSection';

const UnfallgutachtenHamburgPage = () => {
  const [formData, setFormData] = useState({ name: '', phone: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    await fetch(`${API_URL}/api/leads/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...formData, source: 'unfallgutachten-hamburg-landing-page' })
    });
    alert('Vielen Dank! Wir melden uns schnellstmöglich.');
    setFormData({ name: '', phone: '', message: '' });
  };

  const faqs = [
    {
      q: "Wie schnell bekomme ich einen Termin in Hamburg?",
      a: "In Hamburg und der Metropolregion vergeben wir Termine meist innerhalb von 24-48 Stunden. Bei dringenden Fällen – etwa wenn Ihr Fahrzeug nicht fahrbereit ist oder Sie es schnell zur Werkstatt bringen müssen – sind wir oft noch am selben Tag bei Ihnen. Unser Hauptsitz in Rellingen liegt nur 20 Minuten vom Hamburger Stadtrand entfernt, sodass wir sehr flexibel reagieren können."
    },
    {
      q: "Wer trägt die Kosten für das Unfallgutachten in Hamburg?",
      a: "Bei einem unverschuldeten Unfall übernimmt die gegnerische Haftpflichtversicherung die Kosten für das Gutachten vollständig. Sie müssen nicht in Vorleistung gehen und haben das Recht, Ihren Gutachter frei zu wählen – unabhängig davon, was die Versicherung Ihnen sagt. Bei einem verschuldeten Unfall oder Kaskoschaden können wir Sie ebenfalls beraten."
    },
    {
      q: "Welche Stadtteile und Gebiete in Hamburg deckt ihr ab?",
      a: "Wir decken ganz Hamburg ab – von Blankenese bis Bergedorf, von Harburg bis Wandsbek. Auch die gesamte Metropolregion im Umkreis von 50 km gehört zu unserem Einsatzgebiet: Norderstedt, Pinneberg, Ahrensburg, Wedel, Buxtehude, Elmshorn, Reinbek, Geesthacht und alle dazwischenliegenden Gemeinden. Wir kommen dorthin, wo Ihr Fahrzeug steht."
    },
    {
      q: "Kommt ihr auch zu Unfällen auf der Autobahn rund um Hamburg?",
      a: "Ja, selbstverständlich. Unfälle auf der A1, A7, A23, A24 oder dem Hamburger Ring (A255/A252) sind unser Alltag. Wir kommen zur Unfallstelle, zur Werkstatt oder zu Ihnen nach Hause – je nachdem, wo das Fahrzeug nach dem Unfall abgestellt wurde. Auch bei Unfällen im Elbtunnel oder auf den Elbbrücken sind wir schnell vor Ort."
    },
    {
      q: "Was ist der Unterschied zwischen einem Gutachten und einem Kostenvoranschlag?",
      a: "Ein Werkstatt-Kostenvoranschlag enthält nur die reinen Reparaturkosten. Ein unabhängiges Gutachten dokumentiert zusätzlich die Wertminderung (merkantiler Minderwert), den Nutzungsausfall, den Wiederbeschaffungswert bei Totalschaden und sichert alle Ihre Ansprüche rechtssicher. Bei einem Schaden über 750€ lohnt sich ein Gutachten fast immer – die Mehrkosten trägt die gegnerische Versicherung."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Unfallgutachten Hamburg | Kfz-Sachverständige vor Ort | Dein Gutachten Direkt</title>
        <meta name="description" content="Unfallgutachten in Hamburg und Metropolregion. Über 10 Jahre Erfahrung, gerichtsfeste Gutachten. Termin in 24-48h. Kostenlos bei unverschuldetem Unfall. ☎ Jetzt anrufen!" />
        <meta name="keywords" content="Unfallgutachten Hamburg, Kfz Gutachter Hamburg, Sachverständiger Hamburg, Unfallgutachter Hamburg, Kfz Gutachten Hamburg, Gutachter Altona, Gutachter Wandsbek, Gutachter Bergedorf" />
        <link rel="canonical" href="https://www.dein-gutachten-direkt.de/unfallgutachten-hamburg" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Dein Gutachten Direkt - Hamburg",
            "description": "Kfz-Sachverständigenbüro mit über 10 Jahren Erfahrung. Unabhängige Unfallgutachten in Hamburg und der gesamten Metropolregion. Gerichtsfeste Dokumentation, schnelle Termine.",
            "telephone": "+4941018471160",
            "email": "info@dein-gutachten-direkt.de",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Hamburg",
              "addressRegion": "Hamburg",
              "addressCountry": "DE"
            },
            "areaServed": [
              {"@type": "City", "name": "Hamburg"},
              {"@type": "City", "name": "Hamburg-Altona"},
              {"@type": "City", "name": "Hamburg-Wandsbek"},
              {"@type": "City", "name": "Hamburg-Bergedorf"},
              {"@type": "City", "name": "Hamburg-Harburg"},
              {"@type": "City", "name": "Norderstedt"},
              {"@type": "City", "name": "Pinneberg"}
            ],
            "priceRange": "€€"
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": { "@type": "Answer", "text": faq.a }
            }))
          })}
        </script>
      </Helmet>

      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-gray-900 via-black to-gray-800 pt-28 pb-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-green/20 border border-brand-green/30 rounded-full mb-6">
                  <MapPin className="w-4 h-4 text-brand-green" />
                  <span className="text-brand-green font-semibold text-sm">Hamburg & Metropolregion</span>
                </div>

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  Unfallgutachten
                  <br />
                  <span className="text-brand-green">in Hamburg</span>
                </h1>
                
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  Unabhängige Kfz-Sachverständige mit <strong className="text-white">über 10 Jahren Erfahrung</strong>. 
                  Von unserem Hauptsitz in Rellingen sind wir in 20 Minuten in Hamburg – für gerichtsfeste Gutachten direkt vor Ort.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Über 10 Jahre Erfahrung</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Mehrere Sachverständige</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Gerichtsfeste Gutachten</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Zusammenarbeit mit Anwälten</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Alle Hamburger Stadtteile</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Termin oft am selben Tag</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a href={`tel:${COMPANY_INFO.phone}`}>
                    <Button size="lg" className="w-full sm:w-auto bg-brand-green hover:bg-brand-green-dark text-white text-lg px-8 py-6 rounded-xl font-bold">
                      <Phone className="w-5 h-5 mr-2" />
                      Jetzt anrufen
                    </Button>
                  </a>
                  <a href={`https://wa.me/${COMPANY_INFO.whatsapp}`} target="_blank" rel="noopener noreferrer">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6 rounded-xl font-bold">
                      <MessageCircle className="w-5 h-5 mr-2" />
                      WhatsApp
                    </Button>
                  </a>
                </div>

                <p className="text-gray-400 text-sm mt-6">
                  Bei unverschuldetem Unfall: Kostenübernahme durch gegnerische Versicherung
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur border border-white/10 rounded-3xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Einsatzgebiet Hamburg</h2>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Abgedeckte Gebiete</p>
                      <p className="text-gray-400">Ganz Hamburg + 50 km Umkreis</p>
                      <p className="text-gray-400">Alle 7 Bezirke: Mitte, Altona, Eimsbüttel, Nord, Wandsbek, Bergedorf, Harburg</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Clock className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Anfahrt von Rellingen</p>
                      <p className="text-gray-400">20 Min. nach Hamburg-Zentrum</p>
                      <p className="text-gray-400">Termine meist in 24-48 Stunden</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Users className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Unser Team</p>
                      <p className="text-gray-400">Mehrere erfahrene Sachverständige</p>
                      <p className="text-gray-400">Hauptsitz: Rellingen (Kreis Pinneberg)</p>
                    </div>
                  </div>
                </div>

                <div className="bg-brand-green/20 border border-brand-green/30 rounded-xl p-4">
                  <p className="text-brand-green font-semibold text-sm">
                    <strong>Freie Gutachterwahl:</strong> Bei unverschuldetem Unfall dürfen Sie Ihren Gutachter frei wählen – auch wenn die Versicherung anderes behauptet. Die Kosten trägt der Unfallverursacher.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Über uns Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="text-brand-green font-semibold text-sm uppercase tracking-wider">Über uns</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">Ihr Kfz-Gutachter für Hamburg</h2>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed text-lg">
                <strong>Dein Gutachten Direkt</strong> ist Ihr unabhängiger Kfz-Sachverständiger für Hamburg und die Metropolregion. 
                Mit unserem Hauptsitz in <strong>Rellingen</strong> – nur 20 Minuten vom Hamburger Stadtrand entfernt – 
                sind wir ideal positioniert, um Ihnen schnell und unkompliziert zu helfen.
              </p>

              <p className="text-gray-700 leading-relaxed text-lg">
                Seit über <strong>10 Jahren</strong> erstellen wir unabhängige Unfallgutachten für Geschädigte in Hamburg. 
                Ob Auffahrunfall auf der A7, Parkrempler in der Innenstadt oder Wildunfall auf dem Weg nach Bergedorf – 
                wir dokumentieren jeden Schaden <strong>gerichtsfest</strong> und sichern Ihre Ansprüche.
              </p>

              <p className="text-gray-700 leading-relaxed text-lg">
                Unser Team besteht aus <strong>mehreren erfahrenen Sachverständigen</strong>, die alle Fahrzeugtypen begutachten können. 
                Wir arbeiten eng mit <strong>Fachanwälten für Verkehrsrecht</strong> in Hamburg zusammen und können Sie bei Bedarf direkt weitervermitteln.
              </p>

              <div className="bg-gray-50 rounded-2xl p-8 my-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Das zeichnet uns aus:</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Unabhängig:</strong> Wir arbeiten ausschließlich für Sie – nicht für Versicherungen oder Werkstätten</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Erfahren:</strong> Über 10 Jahre Erfahrung mit tausenden Gutachten in Hamburg</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Gerichtsfest:</strong> Unsere Gutachten sind rechtssicher und vor Gericht verwendbar</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Anwaltsnetzwerk:</strong> Enge Zusammenarbeit mit Hamburger Verkehrsrechtsanwälten</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Schnell:</strong> Termine in Hamburg meist innerhalb 24-48 Stunden, oft am selben Tag</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Was beinhaltet unser Gutachten */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="text-brand-green font-semibold text-sm uppercase tracking-wider">Leistungen</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">Was beinhaltet unser Gutachten?</h2>
              <p className="text-xl text-gray-600 mt-4">Ein professionelles Unfallgutachten sichert Ihnen alle Ansprüche gegenüber der Versicherung</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Calculator className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Reparaturkostenkalkulation</h3>
                </div>
                <p className="text-gray-700">Detaillierte Berechnung aller Reparaturkosten nach Herstellervorgaben. Inklusive Arbeitslohn, Original-Ersatzteile, Lackierung und Nebenkosten wie Achsvermessung.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Car className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Wertminderung</h3>
                </div>
                <p className="text-gray-700">Der merkantile Wertverlust Ihres Fahrzeugs nach dem Unfall – auch bei perfekter Reparatur. Bei neueren Fahrzeugen oft mehrere hundert bis tausend Euro zusätzlich.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Nutzungsausfall</h3>
                </div>
                <p className="text-gray-700">Entschädigung für jeden Tag, an dem Sie Ihr Fahrzeug nicht nutzen können. Berechnet nach Fahrzeugklasse – bei einem Mittelklassewagen ca. 35-50€ pro Tag.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Wiederbeschaffungswert</h3>
                </div>
                <p className="text-gray-700">Bei Totalschaden: Was kostet ein gleichwertiges Ersatzfahrzeug auf dem lokalen Hamburger Markt? Wir ermitteln den fairen Marktwert anhand aktueller Vergleichsangebote.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Calculator className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Restwertermittlung</h3>
                </div>
                <p className="text-gray-700">Der Verkaufswert Ihres beschädigten Fahrzeugs bei Totalschaden. Wichtig für die korrekte Abrechnung – wir holen mehrere Restwertangebote ein.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Fotodokumentation</h3>
                </div>
                <p className="text-gray-700">Umfangreiche Beweissicherung mit 30-50 professionellen Fotos aller Schäden aus verschiedenen Winkeln. Inklusive Detailaufnahmen und Übersichtsbilder.</p>
              </div>
            </div>

            <div className="bg-brand-green/10 border-2 border-brand-green/30 rounded-2xl p-6 mt-8 text-center">
              <p className="text-gray-900 font-semibold">
                <strong>Wichtig:</strong> Ein Werkstatt-Kostenvoranschlag enthält nur die Reparaturkosten. 
                Wertminderung, Nutzungsausfall und eine rechtssichere Dokumentation fehlen – das kann Sie bei einem Mittelklassewagen schnell 500-1.500€ kosten!
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Häufige Fragen zu Unfallgutachten in Hamburg</h2>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-3 text-lg">{faq.q}</h3>
                  <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stadtteile */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Alle Hamburger Stadtteile & Umgebung</h2>
            <p className="text-lg text-gray-600 mb-8">Von unserem Hauptsitz in Rellingen erreichen wir jeden Winkel Hamburgs in maximal 45 Minuten</p>
            <div className="flex flex-wrap justify-center gap-3">
              {['Altona', 'Eimsbüttel', 'Hamburg-Nord', 'Wandsbek', 'Bergedorf', 'Harburg', 'Hamburg-Mitte', 'Blankenese', 'Winterhude', 'Eppendorf', 'Barmbek', 'Ottensen', 'St. Pauli', 'HafenCity', 'Wilhelmsburg', 'Finkenwerder', 'Norderstedt', 'Pinneberg', 'Ahrensburg', 'Wedel'].map((ort) => (
                <span key={ort} className="px-4 py-2 bg-white rounded-lg border border-gray-200 text-gray-700 font-medium hover:border-brand-green/50 transition-colors">
                  {ort}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-br from-gray-900 to-black">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Jetzt Gutachten in Hamburg beauftragen</h2>
            <p className="text-xl text-gray-300 mb-8">Hamburg & 50 km Umgebung • Termin meist innerhalb 24-48 Stunden</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${COMPANY_INFO.phone}`}>
                <Button size="lg" className="bg-brand-green hover:bg-brand-green-dark text-white text-lg px-10 py-6 rounded-xl font-bold">
                  <Phone className="w-5 h-5 mr-2" />
                  04101 847 11 60
                </Button>
              </a>
              <a href={`https://wa.me/${COMPANY_INFO.whatsapp}`} target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg px-10 py-6 rounded-xl font-bold">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </section>

        <InternalLinksSection currentPage="/unfallgutachten-hamburg" />
      </main>

      <Footer />
    </div>
  );
};

export default UnfallgutachtenHamburgPage;
